/**
 * Director Monthly Review — Executive BI tab
 *
 * Read-only monthly performance report for the monthly Directors' meeting.
 * Anchors on the last completed calendar month and compares it to the
 * month before that. Sorted lowest → highest by clinic utilization so
 * ODs can walk through the weakest clinic first, then work up.
 *
 * No financial figures. All data comes from
 * `GET /api/exec-bi/director-monthly-review` which reads bi_attendance,
 * clients, clinicians, and perf_targets. Office Directors are excluded
 * from the per-staff performance table; the roster summary counts them.
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import api from '../lib/api';
import {
  Users, Building2, ArrowUpRight, ArrowDownRight, Trophy, Printer,
  CheckCircle2, AlertTriangle, ChevronRight, Award, Activity, Inbox,
  CalendarCheck, TrendingUp, Sparkles, XCircle,
} from 'lucide-react';

const C = {
  ink: '#2D2833', ink2: '#685B72', soft: '#685B72',
  border: '#E9E3DA', hair: '#B08A3A',
  gold: '#B08A3A', purple: '#685B72',
  teal: '#3F7D6E', rose: '#8A3A3A', amber: '#B08A3A',
  paper: '#FBF8F4',
};

const NUM = (n) => (n == null ? '—' : Number(n).toLocaleString());
const PCT = (v, digits = 1) => (v == null ? '—' : `${(Number(v) * 100).toFixed(digits)}%`);
const MONTH_LABEL = (ymd) => {
  if (!ymd) return '—';
  const [y, m] = String(ymd).split('-');
  const names = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const idx = Math.max(0, Math.min(11, Number(m) - 1));
  return `${names[idx]} ${y}`;
};

function DeltaChip({ current, prior, invert = false, format = 'pct', digits = 1 }) {
  if (current == null || prior == null || isNaN(Number(current)) || isNaN(Number(prior))) return null;
  const diff = Number(current) - Number(prior);
  if (Math.abs(diff) < 0.0001 && (format === 'pct' || format === 'pp')) return <span className="text-[10px]" style={{ color: C.soft }}>—</span>;
  const positive = invert ? diff < 0 : diff > 0;
  const color = positive ? C.teal : diff === 0 ? C.soft : C.rose;
  const Icon = diff >= 0 ? ArrowUpRight : ArrowDownRight;
  let text = '';
  if (format === 'pp') text = `${diff >= 0 ? '+' : ''}${(diff * 100).toFixed(digits)}pt`;
  else if (format === 'pct') text = `${diff >= 0 ? '+' : ''}${(prior ? (diff / prior) * 100 : 0).toFixed(digits)}%`;
  else text = `${diff >= 0 ? '+' : ''}${Number(diff).toLocaleString(undefined, { maximumFractionDigits: digits })}`;
  return (
    <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold" style={{ color }}>
      <Icon size={11} strokeWidth={2.4} />{text}
    </span>
  );
}

function KPI({ label, curr, prior, sub, invert = false, format = 'num', icon: Icon, tone, testid, record }) {
  const display = (v) => {
    if (v == null || isNaN(Number(v))) return '—';
    if (format === 'pct' || format === 'pp') return PCT(v);
    return NUM(v);
  };
  const priorDelta = format === 'pct' ? 'pp' : format;   // pct value uses pp delta by default
  return (
    <Card data-testid={testid} className="p-4 rounded-2xl bg-white border relative overflow-hidden" style={{ borderColor: C.border }}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-[9px] uppercase tracking-[0.14em] font-semibold flex items-center gap-1" style={{ color: C.soft }}>
            {label}
            {record && (
              <span title="Best of the year so far" className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[8px] font-bold" style={{ background: '#F5EBD5', color: C.gold }}>
                <Trophy size={9} strokeWidth={2.4} /> RECORD
              </span>
            )}
          </div>
          <div className="mt-1.5 font-serif text-[26px] leading-none tabular-nums" style={{ color: C.ink }}>{display(curr)}</div>
          {prior != null && !isNaN(Number(prior)) && (
            <div className="mt-1.5 flex items-center gap-2 text-[10px]" style={{ color: C.soft }}>
              <span>prior <span className="tabular-nums" style={{ color: C.ink2 }}>{display(prior)}</span></span>
              <DeltaChip current={curr} prior={prior} invert={invert} format={priorDelta} />
            </div>
          )}
          {sub && <div className="text-[10px] mt-1" style={{ color: C.soft }}>{sub}</div>}
        </div>
        {Icon && (
          <div className="rounded-xl p-2 shrink-0" style={{ background: `${tone || C.purple}15` }}>
            <Icon size={14} strokeWidth={1.7} style={{ color: tone || C.purple }} />
          </div>
        )}
      </div>
    </Card>
  );
}

function ClinicCard({ c, minExpected, testid }) {
  const [open, setOpen] = useState(true);
  const utilColor = c.utilization >= minExpected ? C.teal : c.utilization >= 0.70 ? C.amber : C.rose;
  const delta = c.utilization - c.prior_utilization;
  return (
    <Card className="rounded-2xl bg-white border overflow-hidden print:break-inside-avoid" style={{ borderColor: C.border }} data-testid={testid}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex flex-wrap items-center gap-4 px-5 py-4 text-left hover:bg-[#FBF8F4] transition"
        data-testid={`${testid}-toggle`}
      >
        <ChevronRight
          size={16}
          className="transition-transform shrink-0"
          style={{ transform: open ? 'rotate(90deg)' : 'none', color: C.soft }}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.18em] font-bold" style={{ color: utilColor }}>
            <Building2 size={12} strokeWidth={2.4} /> {c.clinic}
          </div>
          <div className="mt-0.5 text-[11px]" style={{ color: C.soft }}>
            {c.staff_count} salaried · {NUM(c.total_shows)} shows of {NUM(Math.round(c.total_target))} target · show rate {PCT(c.show_rate)}
          </div>
        </div>
        <div className="text-right shrink-0">
          <div className="font-serif tabular-nums leading-none" style={{ color: utilColor, fontSize: 26 }}>
            {PCT(c.utilization)}
          </div>
          <div className="text-[10px] mt-1" style={{ color: C.soft }}>
            prior {PCT(c.prior_utilization)}
            <span className="ml-2" style={{ color: delta >= 0 ? C.teal : C.rose, fontWeight: 700 }}>
              {delta >= 0 ? '▲' : '▼'} {Math.abs(delta * 100).toFixed(1)}pt
            </span>
          </div>
        </div>
        <div className="text-right shrink-0 pl-3 border-l" style={{ borderColor: C.hair }}>
          <div className="font-serif tabular-nums leading-none" style={{ color: c.target_hit_pct >= 0.5 ? C.teal : C.rose, fontSize: 20 }}>
            {c.target_hit_count}/{c.staff_count}
          </div>
          <div className="text-[10px] mt-1" style={{ color: C.soft }}>hit target ({PCT(minExpected, 0)}+)</div>
        </div>
      </button>

      {open && (
        <div className="border-t px-5 py-4" style={{ borderColor: C.border }}>
          {/* Cancellation snapshot for this clinic */}
          <div className="grid grid-cols-4 gap-2 mb-4 text-[11px]">
            <div className="rounded-lg px-3 py-2 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Client cancels</div>
              <div className="font-serif text-lg mt-0.5" style={{ color: C.amber }}>{NUM(c.client_cancels)}</div>
            </div>
            <div className="rounded-lg px-3 py-2 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>No-shows</div>
              <div className="font-serif text-lg mt-0.5" style={{ color: C.rose }}>{NUM(c.no_shows)}</div>
            </div>
            <div className="rounded-lg px-3 py-2 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Clinician cancels</div>
              <div className="font-serif text-lg mt-0.5" style={{ color: C.rose }}>{NUM(c.clinician_cancels)}</div>
            </div>
            <div className="rounded-lg px-3 py-2 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Total shows</div>
              <div className="font-serif text-lg mt-0.5" style={{ color: C.teal }}>{NUM(c.total_shows)}</div>
            </div>
          </div>

          {/* Staff table — sorted worst → best inside each clinic */}
          <div className="overflow-auto rounded-lg border" style={{ borderColor: C.border }}>
            <table className="w-full text-[12px]" data-testid={`${testid}-staff-table`}>
              <thead>
                <tr className="text-[9px] uppercase tracking-widest" style={{ background: C.paper, color: C.soft }}>
                  <th className="text-left py-2 px-3">Staff</th>
                  <th className="text-left py-2 px-3">Title</th>
                  <th className="text-left py-2 px-2">FT/PT</th>
                  <th className="text-left py-2 px-2">Bilingual</th>
                  <th className="text-right py-2 px-2">Weekly tgt</th>
                  <th className="text-right py-2 px-2">Monthly tgt</th>
                  <th className="text-right py-2 px-2">Shows</th>
                  <th className="text-right py-2 px-2">CxL / NS / ClnCxl</th>
                  <th className="text-right py-2 px-3">Util</th>
                  <th className="text-right py-2 px-3">MoM</th>
                  <th className="text-center py-2 px-2">Hit</th>
                </tr>
              </thead>
              <tbody>
                {c.staff.map((s, i) => {
                  const util = s.utilization || 0;
                  const utilC = util >= minExpected ? C.teal : util >= 0.70 ? C.amber : C.rose;
                  const d = (s.utilization || 0) - (s.prior_utilization || 0);
                  return (
                    <tr key={s.name} className="border-t" style={{ borderColor: C.hair }} data-testid={`${testid}-row-${i}`}>
                      <td className="py-2 px-3 font-medium" style={{ color: C.ink }}>{s.name}</td>
                      <td className="py-2 px-3" style={{ color: C.ink2 }}>{s.role}</td>
                      <td className="py-2 px-2" style={{ color: C.soft }}>{s.full_time ? 'FT' : 'PT'}</td>
                      <td className="py-2 px-2" style={{ color: s.bilingual ? C.teal : C.soft }}>{s.bilingual ? 'Yes' : '—'}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ color: C.ink2 }}>{NUM(s.weekly_target)}</td>
                      <td className="py-2 px-2 text-right tabular-nums" style={{ color: C.soft }}>{NUM(Math.round(s.monthly_target))}</td>
                      <td className="py-2 px-2 text-right tabular-nums font-semibold" style={{ color: C.ink }}>{NUM(s.shows)}</td>
                      <td className="py-2 px-2 text-right tabular-nums text-[11px]" style={{ color: C.soft }}>
                        {NUM(s.client_cancels)} / {NUM(s.no_shows)} / {NUM(s.clinician_cancels)}
                      </td>
                      <td className="py-2 px-3 text-right tabular-nums font-semibold" style={{ color: utilC }}>{PCT(util)}</td>
                      <td className="py-2 px-3 text-right text-[11px]" style={{ color: d >= 0 ? C.teal : C.rose, fontWeight: 700 }}>
                        {d >= 0 ? '▲' : '▼'} {Math.abs(d * 100).toFixed(1)}pt
                      </td>
                      <td className="py-2 px-2 text-center">
                        {s.hit_target
                          ? <CheckCircle2 size={15} style={{ color: C.teal, display: 'inline' }} />
                          : <XCircle size={15} style={{ color: C.rose, display: 'inline' }} />}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function DirectorMonthlyReview() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState('');

  const load = useCallback((m) => {
    setLoading(true);
    const qs = m ? `?month=${encodeURIComponent(m)}` : '';
    api.get(`/exec-bi/director-monthly-review${qs}`)
      .then((r) => {
        setData(r.data);
        if (!selectedMonth) setSelectedMonth(r.data?.current_month || '');
      })
      .catch((e) => setErr(e?.response?.data?.detail || 'Failed to load review'))
      .finally(() => setLoading(false));
  }, [selectedMonth]);
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onMonthChange = (m) => {
    setSelectedMonth(m);
    load(m);
  };
  const doPrint = () => window.print();

  if (loading) {
    return <div className="p-12 text-center" style={{ color: C.soft }} data-testid="director-review-loading">Loading Director Monthly Review…</div>;
  }
  if (err || !data) {
    return (
      <Card className="p-8 rounded-2xl bg-white border text-center" style={{ borderColor: C.border }} data-testid="director-review-error">
        <AlertTriangle size={22} className="mx-auto mb-2" style={{ color: C.rose }} />
        <div className="font-serif text-lg" style={{ color: C.ink }}>Could not load Director Monthly Review</div>
        <div className="text-sm mt-1" style={{ color: C.soft }}>{err || 'Unexpected error'}</div>
        <Button className="mt-4 rounded-full" onClick={load}>Retry</Button>
      </Card>
    );
  }

  const cur = data.summary?.current || {};
  const pri = data.summary?.prior || {};
  const staff = data.staff_counts || {};
  const clinics = data.clinics || [];
  const records = data.records || [];
  const minExpected = data.min_expected_pct || 0.90;
  const recordSet = new Set(records.filter((r) => r.is_current).map((r) => r.metric));

  return (
    <div data-testid="director-review-root" className="print:bg-white">
      {/* Header — clean, print-optimized */}
      <div className="flex flex-wrap items-end justify-between gap-4 mb-5 print:mb-6">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: C.gold }}>
            <Trophy size={12} /> Monthly Directors Review
          </div>
          <h2 className="font-serif mt-1 leading-none" style={{ color: C.ink, fontSize: 'clamp(24px, 3vw, 32px)' }}>
            {MONTH_LABEL(data.current_month)} <span style={{ color: C.soft, fontSize: '0.65em' }}>vs {MONTH_LABEL(data.prior_month)}</span>
          </h2>
          <div className="text-[11px] mt-2" style={{ color: C.soft }}>
            {data.current_biz_days} business days this month · {data.prior_biz_days} business days prior · clinics sorted lowest → highest utilization · Office Directors excluded from performance table
          </div>
        </div>
        <div className="flex gap-2 print:hidden items-center">
          <div className="flex items-center gap-2">
            <label htmlFor="dmr-month-select" className="text-[10px] uppercase tracking-widest font-semibold" style={{ color: C.soft }}>Report month</label>
            <select
              id="dmr-month-select"
              data-testid="director-review-month-select"
              value={selectedMonth || data.current_month}
              onChange={(e) => onMonthChange(e.target.value)}
              className="h-10 px-3 rounded-full border text-[13px] font-medium bg-white"
              style={{ borderColor: C.hair, color: C.ink }}
            >
              {(data.available_months || [data.current_month]).map((m) => (
                <option key={m} value={m}>{MONTH_LABEL(m)}</option>
              ))}
            </select>
          </div>
          <Button onClick={doPrint} data-testid="director-review-print-btn" className="rounded-full h-10 px-5" variant="outline">
            <Printer size={14} className="mr-2" /> Print / PDF
          </Button>
        </div>
      </div>

      {/* MAJOR KPI SUMMARY — first block per user spec */}
      <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-6 gap-3 mb-3" data-testid="director-review-kpi-summary">
        <KPI testid="dmr-kpi-sessions"        label="Total Sessions"      curr={cur.sessions_total}   prior={pri.sessions_total}   sub="Shows + LC + NS" icon={Activity}      tone={C.purple} record={recordSet.has('sessions_total')} />
        <KPI testid="dmr-kpi-shows"           label="Shows"               curr={cur.shows}            prior={pri.shows}            sub="Attended visits"          icon={CheckCircle2}  tone={C.teal}   record={recordSet.has('shows')} />
        <KPI testid="dmr-kpi-referrals"       label="Referrals"           curr={cur.referrals}        prior={pri.referrals}        sub="New leads received"       icon={Inbox}         tone={C.purple} record={recordSet.has('referrals')} />
        <KPI testid="dmr-kpi-evals"           label="Evals Completed"     curr={cur.evals_completed}  prior={pri.evals_completed}  sub="eval_completed = Yes"     icon={CalendarCheck} tone={C.gold}   record={recordSet.has('evals')} />
        <KPI testid="dmr-kpi-therapy-started" label="Therapy Started"     curr={cur.therapy_started}  prior={pri.therapy_started}  sub="First therapy visit"      icon={TrendingUp}    tone={C.teal}   record={recordSet.has('therapy_started')} />
        <KPI testid="dmr-kpi-utilization"     label="Group Utilization"   curr={cur.group_utilization} prior={pri.group_utilization} sub="Shows ÷ prorated target" icon={Award}         tone={C.gold}   format="pct" />
        <KPI testid="dmr-kpi-show-rate"       label="Show Rate"           curr={cur.show_rate}        prior={pri.show_rate}        sub="Shows ÷ Scheduled"        icon={Sparkles}      tone={C.teal}   format="pct" record={recordSet.has('show_rate')} />
        <KPI testid="dmr-kpi-clinician-cxl"   label="Clinician Cancels"   curr={cur.clinician_cancels} prior={pri.clinician_cancels} sub="Excluded from client attendance" invert icon={AlertTriangle} tone={C.rose} record={recordSet.has('clinician_cancels')} />
        <KPI testid="dmr-kpi-late-cxl"        label="Late Cancels"        curr={cur.late_cancels}     prior={pri.late_cancels}     sub="Client-side"              icon={AlertTriangle} tone={C.amber} invert />
        <KPI testid="dmr-kpi-no-shows"        label="No-Shows"            curr={cur.no_shows}         prior={pri.no_shows}         sub="Client didn't arrive"     icon={XCircle}       tone={C.rose}   invert />
        <KPI testid="dmr-kpi-target-compliance" label="Target Compliance" curr={cur.target_compliance_pct} prior={pri.target_compliance_pct} sub={`${cur.target_compliance_count}/${cur.salaried_total} salaried hit ${PCT(minExpected, 0)}+`} icon={Award} tone={C.gold} format="pct" />
        <KPI testid="dmr-kpi-client-cxl"      label="Client Cancels"      curr={cur.client_cancels}   prior={pri.client_cancels}   sub="Standard cancel"          icon={AlertTriangle} tone={C.amber} invert />
      </div>

      {/* STAFF ROSTER SUMMARY */}
      <Card className="p-5 rounded-2xl mb-6 bg-white border" style={{ borderColor: C.border }} data-testid="director-review-staff-summary">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-2" style={{ color: C.gold }}>
              <Users size={12} strokeWidth={2.4} /> Staff Roster · active clinicians as of {MONTH_LABEL(data.current_month)}
            </div>
            <div className="text-[11px] mt-1" style={{ color: C.soft }}>
              Excludes clinicians terminated before the month began. Office Directors counted here but excluded from the per-staff performance table below.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <div className="rounded-lg px-3 py-1.5 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Total active</div>
              <div className="font-serif text-lg tabular-nums" style={{ color: C.ink }}>{NUM(staff.total_active)}</div>
            </div>
            <div className="rounded-lg px-3 py-1.5 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Full-time</div>
              <div className="font-serif text-lg tabular-nums" style={{ color: C.teal }}>{NUM(staff.full_time)}</div>
            </div>
            <div className="rounded-lg px-3 py-1.5 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Part-time</div>
              <div className="font-serif text-lg tabular-nums" style={{ color: C.amber }}>{NUM(staff.part_time)}</div>
            </div>
            <div className="rounded-lg px-3 py-1.5 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Bilingual</div>
              <div className="font-serif text-lg tabular-nums" style={{ color: C.purple }}>{NUM(staff.bilingual)}</div>
            </div>
            <div className="rounded-lg px-3 py-1.5 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Office Directors</div>
              <div className="font-serif text-lg tabular-nums" style={{ color: C.gold }}>{NUM(staff.office_directors_excluded)}</div>
            </div>
            <div className="rounded-lg px-3 py-1.5 border" style={{ borderColor: C.border, background: C.paper }}>
              <div className="text-[9px] uppercase tracking-widest" style={{ color: C.soft }}>Salaried tracked</div>
              <div className="font-serif text-lg tabular-nums" style={{ color: C.ink }}>{NUM(staff.salaried_included)}</div>
            </div>
          </div>
        </div>

        {/* By title */}
        <div className="mt-4 flex flex-wrap gap-2" data-testid="director-review-titles">
          {(staff.by_title || []).map((t) => (
            <span key={t.role} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full border text-[11px]" style={{ borderColor: C.hair, background: C.paper }} data-testid={`dmr-title-${t.role.replace(/\W+/g, '-').toLowerCase()}`}>
              <span style={{ color: C.ink }}>{t.role}</span>
              <span className="font-serif tabular-nums" style={{ color: C.gold }}>{t.count}</span>
            </span>
          ))}
        </div>
      </Card>

      {/* RECORDS — highlight any record-setting metric for the year */}
      {records.length > 0 && (
        <Card className="p-5 rounded-2xl mb-6 border" style={{ background: 'linear-gradient(135deg, #F7EFDA 0%, #FFFFFF 65%)', borderColor: '#B08A3A55' }} data-testid="director-review-records">
          <div className="text-[10px] uppercase tracking-[0.18em] font-bold flex items-center gap-2" style={{ color: C.gold }}>
            <Trophy size={12} strokeWidth={2.4} /> Records set this year
          </div>
          <div className="mt-3 grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3">
            {records.map((r) => {
              const isCurrent = r.is_current;
              const label = {
                sessions_total: 'Most sessions',
                shows: 'Most shows',
                referrals: 'Most referrals',
                evals: 'Most evals completed',
                therapy_started: 'Most therapy starts',
                show_rate: 'Best show rate',
                clinician_cancels: 'Fewest clinician cxl',
              }[r.metric] || r.metric;
              const val = r.metric === 'show_rate' ? PCT(r.value) : NUM(r.value);
              return (
                <div key={r.metric} className="rounded-xl p-3 border bg-white flex flex-col gap-1" style={{ borderColor: isCurrent ? C.gold : C.border, boxShadow: isCurrent ? `0 0 0 2px ${C.gold}22` : 'none' }} data-testid={`dmr-record-${r.metric}`}>
                  <div className="text-[9px] uppercase tracking-widest font-semibold flex items-center gap-1" style={{ color: isCurrent ? C.gold : C.soft }}>
                    {isCurrent && <Sparkles size={10} strokeWidth={2.4} />}{label}
                  </div>
                  <div className="font-serif text-lg tabular-nums" style={{ color: C.ink }}>{val}</div>
                  <div className="text-[10px]" style={{ color: isCurrent ? C.gold : C.soft, fontWeight: isCurrent ? 700 : 400 }}>
                    {MONTH_LABEL(r.month)} {isCurrent && '· this month!'}
                  </div>
                </div>
              );
            })}
          </div>
        </Card>
      )}

      {/* PER-CLINIC BREAKDOWN — sorted lowest → highest by utilization */}
      <div className="mb-4">
        <div className="flex items-end justify-between">
          <div>
            <h3 className="font-serif text-xl leading-none" style={{ color: C.ink }}>Clinic-by-clinic walk-through</h3>
            <div className="text-[11px] mt-1.5" style={{ color: C.soft }}>Ranked lowest → highest utilization · click a clinic to expand or collapse its salaried staff</div>
          </div>
          <div className="text-[11px]" style={{ color: C.soft }}>
            {clinics.length} clinics · {staff.salaried_included} salaried
          </div>
        </div>
      </div>

      <div className="space-y-3">
        {clinics.map((c, i) => (
          <ClinicCard key={c.clinic} c={c} minExpected={minExpected} testid={`dmr-clinic-${i}`} />
        ))}
        {clinics.length === 0 && (
          <Card className="p-8 rounded-2xl bg-white border text-center" style={{ borderColor: C.border }} data-testid="dmr-no-clinics">
            <div className="font-serif text-lg" style={{ color: C.ink }}>No salaried staff in the roster for this month</div>
            <div className="text-sm mt-1" style={{ color: C.soft }}>Ensure clinicians have a weekly target set in Performance and are not marked as Office Directors.</div>
          </Card>
        )}
      </div>

      <div className="text-center text-[10px] uppercase tracking-widest mt-6" style={{ color: C.soft }}>
        Generated {new Date(data.generated_at).toLocaleString()} · Read-only view — no financial figures
      </div>

      {/* Print CSS — keep the report tidy when exported */}
      <style>{`
        @media print {
          .print\\:hidden { display: none !important; }
          [data-testid="director-review-root"] {
            font-size: 10px;
            color: #000;
            background: #fff;
          }
          .print\\:break-inside-avoid { break-inside: avoid; }
        }
      `}</style>
    </div>
  );
}
